import {
  getArchesCoveredExactly,
  groupAdjacentTeeth,
  groupAndOrderTeethByQuadrants,
} from "@jasper/client-models";
import type { ProductDescriptionT } from "./describable.types";

const TEETH_SPAN_SEPARATOR = "–";

const lowestTooth = (teeth: number[]) => Math.min(...teeth);

const formatTeethGroup = (teeth: number[]) => {
  const first = lowestTooth(teeth);
  const last = Math.max(...teeth);

  return first === last ? `${first}` : `${first}${TEETH_SPAN_SEPARATOR}${last}`;
};

// « 15 », « 14–16 », « 11–13, 21–23 » (never across the midline), or the arch names when whole arches are covered
export const formatTeeth = (
  teeth: ReadonlyArray<number>,
  t: ProductDescriptionT
): string => {
  const uniqueTeeth = [...new Set(teeth)];

  if (!uniqueTeeth.length) {
    return "";
  }

  const coveredArches = getArchesCoveredExactly(uniqueTeeth);
  if (coveredArches.length) {
    return coveredArches.map(arch => t(`arches.${arch}`)).join(", ");
  }

  const teethByQuadrant = Object.values(
    groupAndOrderTeethByQuadrants(uniqueTeeth)
  );
  const chartTeeth = teethByQuadrant.flat();
  // Numbers outside the FDI chart are kept as is rather than silently dropped
  const otherTeeth = uniqueTeeth
    .filter(tooth => !chartTeeth.includes(tooth))
    .sort((a, b) => a - b);

  return [
    ...teethByQuadrant.flatMap(quadrantTeeth =>
      groupAdjacentTeeth(quadrantTeeth)
        .sort((a, b) => lowestTooth(a) - lowestTooth(b))
        .map(formatTeethGroup)
    ),
    ...otherTeeth.map(String),
  ].join(", ");
};
